// src/views/WotdView.js — Watch of the Day wall
import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { upsertOne } from "../lib/supabase";
import { compressImage, detectWatchFromPhoto } from "../lib/utils";
import { useLanguage, t } from "../lib/i18n";

const BUCKET = "watch-photos";
const BRANDS = ["HMT", "Allwyn", "Ricoh", "Other"];
const DECADES = ["1950s","1960s","1970s","1980s","1990s","2000s","Unknown"];
const CONDITIONS = ["Mint","Good","Fair","Poor","Parts Only"];

function dateLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day:"numeric", month:"long", year:"numeric" });
}

function groupByDate(posts) {
  const map = new Map();
  for (const p of posts) {
    const key = (p.date || p.created_at || "").slice(0, 10);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }
  return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}

function Avatar({ name, size = 28 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "#111118", border: "1.5px solid #E4E4E7",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Mono',monospace", fontSize: size * 0.38, fontWeight: 700, color: "#fff",
      flexShrink: 0,
    }}>
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

// ─── Post Modal ───────────────────────────────────────────────────────────────

function PostModal({ session, aiEnabled, watchModels, saveWMs, saveCatalog, catalogEntries, toast, onClose, onPosted }) {
  const [step, setStep]       = useState("upload");   // upload | form | submitting
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [aiNote, setAiNote]     = useState(null);
  const [form, setForm]         = useState({ brand:"", model:"", decade:"", condition:"", description:"", caption:"" });
  const fileRef = useRef();

  const norm = v => (!v || v.toLowerCase() === "unknown" ? "" : v);

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setAiNote(null);
    setStep("form");

    if (aiEnabled) {
      setDetecting(true);
      try {
        const res = await detectWatchFromPhoto(f);
        if (res) {
          const brand  = BRANDS.includes(res.brand) ? res.brand : "";
          const model  = norm(res.model);
          const decade = DECADES.includes(res.decade) ? res.decade : norm(res.decade);
          const cond   = CONDITIONS.includes(res.condition) ? res.condition : "";
          setForm(prev => ({
            ...prev,
            brand:       brand       || prev.brand,
            model:       model       || prev.model,
            decade:      decade      || prev.decade,
            condition:   cond        || prev.condition,
            description: norm(res.description) || prev.description,
          }));
          if (!brand && !model) {
            setAiNote("AI couldn't identify this watch — please fill in the details below.");
          } else {
            setAiNote("AI pre-filled below — review and correct if needed.");
          }
        } else {
          setAiNote("AI detection unavailable — fill in manually.");
        }
      } catch (_) {
        setAiNote("AI detection failed — fill in manually.");
      }
      setDetecting(false);
    }
  }, [aiEnabled]);

  const handleDrop = useCallback(e => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith("image/")) handleFile(f);
  }, [handleFile]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    if (!form.brand || !form.model) { toast("Brand and model are required"); return; }
    setStep("submitting");
    try {
      const u = session.user;
      const postId = "wotd-" + Date.now();

      // Upload photo
      const compressed = await compressImage(file, 1200, 0.84);
      const path = `wotd/${postId}.jpg`;
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, compressed, { upsert: true, contentType: "image/jpeg" });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path);

      // Upsert watch_model
      const wmKey = `${form.brand.toLowerCase().trim()}|||${form.model.toLowerCase().trim()}`;
      let wm = (watchModels || []).find(w => `${(w.brand||"").toLowerCase().trim()}|||${(w.model||"").toLowerCase().trim()}` === wmKey);
      let watchModelId = wm?.id;
      if (!watchModelId) {
        watchModelId = "wm-" + Date.now();
        const newWm = { id: watchModelId, brand: form.brand, model: form.model, decade: form.decade || "", movement: "", photos: [publicUrl], createdAt: new Date().toISOString() };
        await upsertOne("watch_models", newWm);
        if (saveWMs) saveWMs([...(watchModels || []), newWm]);
      }

      // Create catalog entry (approved immediately)
      const entryId = "ce-wotd-" + Date.now();
      const entry = {
        id: entryId, brand: form.brand, model: form.model, decade: form.decade,
        condition: form.condition, description: form.description,
        photos: [publicUrl], status: "approved", entryType: "wotd",
        watchModelId, collectorId: u.id, collectorName: u.name || u.email,
        createdAt: new Date().toISOString(),
      };
      await upsertOne("catalog_entries", entry);
      if (saveCatalog) saveCatalog([...(catalogEntries || []), entry]);

      // Insert wotd_post
      const today = new Date().toISOString().slice(0, 10);
      await supabase.from("wotd_posts").insert({
        id: postId, user_id: u.id, user_name: u.name || u.email,
        date: today, photo_url: publicUrl, watch_model_id: watchModelId,
        catalog_entry_id: entryId, caption: form.caption || null,
        brand: form.brand, model: form.model,
        created_at: new Date().toISOString(),
      });

      toast("Watch posted to the wall!");
      onPosted();
      onClose();
    } catch (err) {
      console.error(err);
      toast("Something went wrong — try again");
      setStep("form");
    }
  };

  const inputStyle = { width:"100%", boxSizing:"border-box", padding:"9px 12px", border:"1.5px solid var(--bd)", borderRadius:10, fontSize:13, fontFamily:"'DM Sans',sans-serif", background:"var(--bg)", color:"var(--ink)", outline:"none" };
  const labelStyle = { fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:"0.08em", color:"var(--ink3)", display:"block", marginBottom:5 };

  return (
    <>
      <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:300,background:"rgba(0,0,0,.55)",backdropFilter:"blur(4px)"}}/>
      <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",zIndex:301,background:"#fff",borderRadius:20,width:"min(94vw,520px)",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,.2)"}}>

        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 24px 0"}}>
          <div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontStyle:"italic",color:"#111118"}}>Post a Watch</div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginTop:2}}>SHARE YOUR PIECE WITH THE COMMUNITY</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--ink3)",lineHeight:1}}>×</button>
        </div>

        <div style={{padding:"20px 24px 24px"}}>

          {/* Upload step */}
          {step === "upload" && (
            <div
              onDrop={handleDrop}
              onDragOver={e=>e.preventDefault()}
              onClick={()=>fileRef.current.click()}
              style={{border:"2px dashed var(--bd)",borderRadius:14,padding:"52px 24px",textAlign:"center",cursor:"pointer",background:"var(--bg)",transition:"border-color .15s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor="var(--accent)"}
              onMouseLeave={e=>e.currentTarget.style.borderColor="var(--bd)"}>
              <div style={{fontSize:36,marginBottom:12}}>📷</div>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"var(--ink3)",letterSpacing:"0.08em"}}>TAP OR DRAG A PHOTO</div>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:"var(--ink3)",marginTop:6}}>JPG, PNG, HEIC · max 10MB</div>
              <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>{ const f=e.target.files?.[0]; if(f) handleFile(f); }}/>
            </div>
          )}

          {/* Form step */}
          {(step === "form" || step === "submitting") && (
            <>
              {/* Photo preview */}
              {preview && (
                <div style={{height:200,background:"#F4F4F5",borderRadius:12,overflow:"hidden",marginBottom:18,position:"relative"}}>
                  <img src={preview} alt="watch" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                  {detecting && (
                    <div style={{position:"absolute",inset:0,background:"rgba(255,255,255,.82)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:10}}>
                      <div style={{width:28,height:28,border:"2.5px solid #059669",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.1em"}}>AI DETECTING WATCH…</div>
                      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                    </div>
                  )}
                </div>
              )}

              {aiNote && !detecting && (
                <div style={{background: aiNote.startsWith("AI pre-filled") ? "#ECFDF5" : "#FFF7ED", border:`1px solid ${aiNote.startsWith("AI pre-filled")?"#A7F3D0":"#FED7AA"}`, borderRadius:8, padding:"8px 12px", marginBottom:12, fontFamily:"'DM Mono',monospace", fontSize:9, color: aiNote.startsWith("AI pre-filled") ? "#065F46" : "#92400E", letterSpacing:"0.06em"}}>
                  {aiNote.startsWith("AI pre-filled") ? "✓ " : "⚠ "}{aiNote}
                </div>
              )}

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                <div>
                  <label style={labelStyle}>BRAND *</label>
                  <select value={form.brand} onChange={e=>set("brand",e.target.value)} style={{...inputStyle,height:40}}>
                    <option value="">Select</option>
                    {BRANDS.map(b=><option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>MODEL *</label>
                  <input value={form.model} onChange={e=>set("model",e.target.value)} placeholder="e.g. Pilot, Janata" style={inputStyle}/>
                </div>
                <div>
                  <label style={labelStyle}>DECADE</label>
                  <select value={form.decade} onChange={e=>set("decade",e.target.value)} style={{...inputStyle,height:40}}>
                    <option value="">Select</option>
                    {DECADES.map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>CONDITION</label>
                  <select value={form.condition} onChange={e=>set("condition",e.target.value)} style={{...inputStyle,height:40}}>
                    <option value="">Select</option>
                    {CONDITIONS.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div style={{marginBottom:12}}>
                <label style={labelStyle}>DESCRIPTION</label>
                <textarea value={form.description} onChange={e=>set("description",e.target.value)}
                  placeholder="Dial colour, movement type, any history..."
                  rows={3} style={{...inputStyle,resize:"vertical"}}/>
              </div>

              <div style={{marginBottom:20}}>
                <label style={labelStyle}>CAPTION <span style={{color:"var(--ink3)",fontWeight:400}}>(optional)</span></label>
                <input value={form.caption} onChange={e=>set("caption",e.target.value)} placeholder="A story, a memory, a find..." style={inputStyle}/>
              </div>

              <button
                onClick={handleSubmit}
                disabled={step === "submitting" || detecting}
                style={{width:"100%",background:"#111118",color:"#fff",border:"none",borderRadius:12,padding:"13px",fontFamily:"'DM Mono',monospace",fontSize:11,letterSpacing:"0.08em",cursor:"pointer",opacity:(step==="submitting"||detecting)?0.6:1,transition:"opacity .15s"}}>
                {step === "submitting" ? "POSTING…" : "POST TO WALL →"}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

export function WotdView({ session, openAuth, toast, aiEnabled, watchModels, saveWMs, catalogEntries, saveCatalog }) {
  const { lang } = useLanguage();
  const [posts, setPosts]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showPost, setShowPost] = useState(false);
  const [hovCard, setHovCard]   = useState(null);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("wotd_posts").select("*").order("created_at", { ascending: false }).limit(120);
    setPosts(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const grouped = groupByDate(posts);

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:28,flexWrap:"wrap",gap:12}}>
        <div>
          <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.2em",color:"var(--accent)",textTransform:"uppercase"}}>{t("wotd_daily",lang)}</span>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:36,margin:"6px 0 4px",letterSpacing:"-0.025em"}}>{t("wotd_head",lang)}</h1>
          <p style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.08em",margin:0}}>
            {posts.length} WATCHES POSTED BY THE COMMUNITY
          </p>
        </div>
        {session
          ? <button onClick={()=>setShowPost(true)} style={{background:"#111118",color:"#fff",border:"none",borderRadius:50,padding:"11px 22px",fontSize:13,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:8,letterSpacing:"-0.01em"}}>
              <span>📷</span> Post a Watch
            </button>
          : <button onClick={openAuth} style={{background:"var(--bg2)",color:"var(--ink2)",border:"1.5px solid var(--bd)",borderRadius:50,padding:"11px 22px",fontSize:13,cursor:"pointer",fontFamily:"'DM Mono',monospace",letterSpacing:"0.06em",fontSize:10}}>
              SIGN IN TO POST
            </button>
        }
      </div>

      {/* Wall */}
      {loading ? (
        <div style={{textAlign:"center",padding:"80px 0",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.1em"}}>LOADING WALL…</div>
      ) : posts.length === 0 ? (
        <div style={{textAlign:"center",padding:"80px 24px",background:"var(--bg2)",borderRadius:20}}>
          <div style={{fontSize:40,marginBottom:16}}>📷</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontStyle:"italic",marginBottom:8}}>Be the first to post</div>
          <p style={{color:"var(--ink3)",fontSize:13,lineHeight:1.7,maxWidth:320,margin:"0 auto 20px"}}>Share a watch from your collection, a recent find, or a family heirloom.</p>
          {session
            ? <button onClick={()=>setShowPost(true)} style={{background:"#111118",color:"#fff",border:"none",borderRadius:50,padding:"11px 24px",fontSize:13,cursor:"pointer"}}>Post a Watch →</button>
            : <button onClick={openAuth} style={{background:"#111118",color:"#fff",border:"none",borderRadius:50,padding:"11px 24px",fontSize:13,cursor:"pointer"}}>Sign in to post →</button>
          }
        </div>
      ) : (
        <div>
          {grouped.map(([dateKey, dayPosts]) => (
            <div key={dateKey} style={{marginBottom:36}}>
              {/* Date separator */}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontStyle:"italic",color:"var(--ink2)",whiteSpace:"nowrap"}}>{dateLabel(dateKey)}</div>
                <div style={{flex:1,height:1,background:"var(--bd)"}}/>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.1em",whiteSpace:"nowrap"}}>{dayPosts.length} {dayPosts.length===1?"WATCH":"WATCHES"}</div>
              </div>

              {/* Grid */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:14}}>
                {dayPosts.map(p => (
                  <div key={p.id}
                    style={{background:"#fff",border:"1.5px solid var(--bd)",borderRadius:16,overflow:"hidden",boxShadow:hovCard===p.id?"0 8px 28px rgba(0,0,0,.10)":"0 2px 8px rgba(0,0,0,.04)",transition:"box-shadow .2s,transform .2s",transform:hovCard===p.id?"translateY(-3px)":"none",cursor:"default"}}
                    onMouseEnter={()=>setHovCard(p.id)} onMouseLeave={()=>setHovCard(null)}>
                    {/* Photo */}
                    <div style={{height:220,background:"#F4F4F5",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                      {p.photo_url
                        ? <img src={p.photo_url} alt={p.model} style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                        : <span style={{fontSize:32,opacity:.3}}>📷</span>
                      }
                    </div>
                    {/* Info */}
                    <div style={{padding:"13px 15px"}}>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontStyle:"italic",lineHeight:1.25,marginBottom:4,color:"#111118"}}>{p.brand} {p.model}</div>
                      {p.caption && <p style={{fontSize:12,color:"var(--ink3)",lineHeight:1.55,margin:"0 0 10px"}}>{p.caption}</p>}
                      <div style={{display:"flex",alignItems:"center",gap:7,marginTop:8}}>
                        <Avatar name={p.user_name}/>
                        <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:"var(--ink2)",fontWeight:500}}>{p.user_name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showPost && (
        <PostModal
          session={session} aiEnabled={aiEnabled}
          watchModels={watchModels} saveWMs={saveWMs}
          catalogEntries={catalogEntries} saveCatalog={saveCatalog}
          toast={toast}
          onClose={()=>setShowPost(false)}
          onPosted={loadPosts}
        />
      )}
    </div>
  );
}
